import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Loader,
  Modal,
  SimpleGrid,
  Text,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useNotification, useTranslation } from '@refinedev/core';
import { IconUpload } from '@tabler/icons-react';
import { useParseFamilyMedicineFiles } from 'api/action';
import { useReducer, useRef, useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { nowISODate } from 'utils/dateUtils';
import { uploadFileToS3 } from 'utils/fileUpload';

const REQUIRED_KEYS = ['Demographics'] as const;
const OPTIONAL_KEYS = [
  'Asthma',
  'Diabetes',
  'Hypertension',
  'Prevention',
  'CHF',
  'CAD',
  'Osteoporosis',
  'MHA',
  'STBBI',
] as const;

const ALL_FILES = [...REQUIRED_KEYS, ...OPTIONAL_KEYS] as const;
type FileKey = (typeof ALL_FILES)[number];
const FILE_NAMES: Record<FileKey, string> = {
  Demographics: 'demographic.csv',
  Asthma: 'asthma.csv',
  Diabetes: 'diabetes.csv',
  Hypertension: 'hypertension.csv',
  Prevention: 'prevention.csv',
  CHF: 'CHF.csv',
  CAD: 'CAD.csv',
  Osteoporosis: 'osteoporosis.csv',
  MHA: 'MHA.csv',
  STBBI: 'STBBI.csv',
};
type State = Record<FileKey, File | null>;
type Action =
  | { type: 'SET_FILE'; key: FileKey; file: File | null }
  | { type: 'RESET' };

const fileReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_FILE':
      return { ...state, [action.key]: action.file };
    case 'RESET':
      return ALL_FILES.reduce(
        (acc, key) => ({ ...acc, [key]: null }),
        {} as State
      );
    default:
      return state;
  }
};

const initialState: State = ALL_FILES.reduce(
  (acc, key) => ({ ...acc, [key]: null }),
  {} as State
);

export const FamilyMedicineUploadModal = ({ close, opened }) => {
  const { id: siteID } = useCurrentSiteStore();
  const { translate } = useTranslation();

  const openRef = useRef<() => void>(null);
  const parseFamilyMedicine = useParseFamilyMedicineFiles();

  const { open: showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [fileState, dispatch] = useReducer(fileReducer, initialState);
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<FileKey[]>(
    []
  );

  const areRequiredFilesPresent = () =>
    REQUIRED_KEYS.every((key) => fileState[key] !== null) &&
    OPTIONAL_KEYS.some((key) => {
      return selectedOptionalKeys.includes(key) && fileState[key] !== null;
    });

  const handleToggleOptional = (key: FileKey) =>
    setSelectedOptionalKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (!areRequiredFilesPresent()) {
        showNotification?.({
          message: translate('pages:familyMedicineUpload.missingFiles'),
          type: 'error',
        });
        return;
      }
      // Prefix filenames with today's date (YYYY-MM-DD)
      const today = nowISODate(); // e.g. "2024-06-07"
      const selectedFileKeys: FileKey[] = [
        'Demographics',
        ...selectedOptionalKeys,
      ];
      const filesToUpload = selectedFileKeys.flatMap((fileKey) => {
        const file = fileState[fileKey];
        if (!file) return [];
        return [
          new File([file], `${today}/${FILE_NAMES[fileKey]}`, {
            type: file.type,
          }),
        ];
      });
      const uploadResults = await uploadFileToS3(
        filesToUpload,
        `family-medicine-files-${import.meta.env.VITE_ACCOUNT_ID}`
      );
      if (uploadResults.some((result) => result === null)) {
        throw new Error('One or more Family Medicine files failed to upload');
      }

      await parseFamilyMedicine.mutateAsync({
        fileNames: filesToUpload.map((file) => file.name),
        siteID: siteID,
      });

      showNotification?.({
        message: translate('pages:familyMedicineUpload.success'),
        type: 'success',
      });

      dispatch({ type: 'RESET' });
      close();
    } catch (error) {
      showNotification?.({
        message: translate('pages:familyMedicineUpload.error'),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title={translate('pages:familyMedicineUpload.title')}
        size="xl"
        centered
      >
        {isLoading ? (
          <Flex justify="center" align="center" w="100%">
            <Loader size="lg" mb="md" variant="bars" />
          </Flex>
        ) : (
          <Flex direction="column" gap="md" w="100%" h="100%">
            <Title order={5}>
              {translate('pages:familyMedicineUpload.requiredFile')}
            </Title>
            <Dropzone
              openRef={openRef}
              accept={{ 'text/csv': ['.csv'] }}
              onDrop={(files) =>
                dispatch({
                  type: 'SET_FILE',
                  key: 'Demographics',
                  file: files[0],
                })
              }
              maxFiles={1}
            >
              <Flex align="center" justify="center" direction="column" p="md">
                <IconUpload />
                <Text mt="sm">
                  {fileState.Demographics?.name ||
                    translate('pages:familyMedicineUpload.selectRequired')}
                </Text>
              </Flex>
            </Dropzone>

            <Divider my="md" />
            <Title order={5}>
              {translate('pages:familyMedicineUpload.optionalFiles')}
            </Title>
            <Text size="sm" c="dimmed" mb="md">
              {translate('pages:familyMedicineUpload.optionalDescription')}
            </Text>
            <Flex wrap="wrap" gap="sm">
              {OPTIONAL_KEYS.map((key) => (
                <Checkbox
                  key={key}
                  label={key}
                  checked={selectedOptionalKeys.includes(key)}
                  onChange={() => handleToggleOptional(key)}
                />
              ))}
            </Flex>

            <SimpleGrid
              cols={selectedOptionalKeys.length % 2 === 0 ? 2 : 1}
              spacing="md"
              p="md"
              verticalSpacing={'md'}
            >
              {selectedOptionalKeys.map((key) => (
                <Dropzone
                  key={key}
                  accept={{ 'text/csv': ['.csv'] }}
                  onDrop={(files) =>
                    dispatch({ type: 'SET_FILE', key, file: files[0] })
                  }
                  maxFiles={1}
                >
                  <IconUpload />
                  <Text mt="sm">
                    {fileState[key]?.name ||
                      translate('pages:familyMedicineUpload.selectFile', {
                        fileName: FILE_NAMES[key],
                      })}
                  </Text>
                </Dropzone>
              ))}
            </SimpleGrid>
            <Flex justify="center" gap="lg" mt="md">
              <Button
                onClick={() => {
                  dispatch({ type: 'RESET' });
                  close();
                }}
                c="gray"
                color="gray"
                variant="outline"
              >
                {translate('pages:familyMedicineUpload.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!areRequiredFilesPresent()}
              >
                {translate('pages:familyMedicineUpload.submit')}
              </Button>
            </Flex>
          </Flex>
        )}
      </Modal>
    </>
  );
};
